import { io, type Socket } from 'socket.io-client';
import type { EventosCliente, EventosServidor, ItemArrastado, TravarResposta } from '@nexital/shared';
import type { CanalTempoReal, FonteToken, OuvintesTempoReal } from '../application/portas';

type Conexao = Socket<EventosServidor, EventosCliente>;

/**
 * Socket.IO do quadro (ADR-0008). O JWT vai na conexão e é relido a cada reconexão; se o servidor recusar o
 * token, renova a sessão e tenta de novo. Ao (re)entrar na sala, informa o último seq visto para receber o perdido.
 */
export class CanalSocket implements CanalTempoReal {
  private socket: Conexao | null = null;

  constructor(
    private readonly sessao: FonteToken,
    readonly clienteId: string,
  ) {}

  entrar(quadroUuid: string, ouvintes: OuvintesTempoReal) {
    let ultimoSeq: number | null = null;
    const socket: Conexao = io({ path: '/socket.io', auth: (cb) => cb({ token: this.sessao.token }) });
    this.socket = socket;

    socket.on('connect', () => {
      socket.emit('entrar', { quadroUuid, ultimoSeq }, (resposta) => {
        if (resposta.ok) ultimoSeq = Math.max(ultimoSeq ?? 0, resposta.seqAtual);
        ouvintes.aoConectar(resposta);
      });
    });
    socket.on('disconnect', () => ouvintes.aoDesconectar());
    socket.on('connect_error', (erro) => {
      ouvintes.aoDesconectar();
      // Token recusado: o socket não tenta de novo sozinho; renova a sessão e reconecta.
      if (erro.message === 'nao-autenticado') void this.sessao.renovar().then((ok) => ok && socket.connect());
    });
    socket.on('evento', (evento) => {
      ultimoSeq = Math.max(ultimoSeq ?? 0, evento.seq);
      if (evento.cliente !== this.clienteId) ouvintes.aoEvento(evento);
    });
    socket.on('presenca', ouvintes.aoPresenca);
    socket.on('arrastando', ouvintes.aoArraste);
    socket.on('travas', ouvintes.aoTravas);
    socket.on('notificacao', ouvintes.aoNotificacao);

    return () => {
      socket.disconnect();
      if (this.socket === socket) this.socket = null;
    };
  }

  arrastando(itens: ItemArrastado[]) {
    this.socket?.volatile.emit('arrastando', itens);
  }

  travar(chave: string) {
    const socket = this.socket;
    if (!socket?.connected) return Promise.resolve<TravarResposta>({ ok: true });
    return new Promise<TravarResposta>((ok) => socket.timeout(2000).emit('travar', chave, (erro, resposta) => ok(erro ? { ok: true } : resposta)));
  }

  liberar(chave: string) {
    this.socket?.emit('liberar', chave);
  }
}
